import { ComponentPropsWithoutRef, forwardRef, useContext } from "react";
import { motion } from "motion/react";
import { core } from "@servicetitan/hammer-token";
import { TabContext } from "./internal/TabContext";

import cx from "classnames";
import styles from "./Tab.module.scss";

/**
 * Props for the TabPanel component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag">
 */
export type TabPanelProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag"
> & {
  /**
   * Unique identifier for the tab panel.
   */
  id: string;
};

/**
 * TabPanel component for displaying tab content.
 *
 * Features:
 * - Automatic visibility based on active tab
 * - Smooth fade animations
 * - Proper ARIA attributes for accessibility
 * - Automatic association with tab buttons
 * - Focus management
 * - Layout animations
 *
 * @example
 * <Tab.Panel id="panel1">
 *   <h2>First Tab Content</h2>
 *   <p>This is the content for the first tab.</p>
 * </Tab.Panel>
 */
export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(
  (props, ref) => {
    const { id, className, ...rest } = props;
    const { current, items } = useContext(TabContext);

    let labelledby;

    items?.forEach((el) => {
      const controlId = el.getAttribute("aria-controls");
      if (controlId === id) {
        labelledby = el.id;
      }
    });
    const currentId = items?.[current]?.getAttribute("aria-controls");
    const isCurrent = currentId === id;

    const tabPanelClassNames = cx(styles["tab-panel"], className);

    if (!isCurrent) return null;
    return (
      <motion.div
        className={tabPanelClassNames}
        transition={{
          opacity: {
            duration:
              +core.primitive.DurationSlow.value.replace("ms", "") / 1000,
          },
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        layout
        {...rest}
        role="tabpanel"
        data-anv="tab-panel"
        id={id}
        aria-labelledby={labelledby}
        tabIndex={0}
        ref={ref}
      />
    );
  },
);

TabPanel.displayName = "TabPanel";
